import React from 'react';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { FileText, CheckCircle, XCircle, CreditCard, ShieldCheck, Scale, Clock, AlertTriangle, Mail } from 'lucide-react';

export const Terms = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid="terms-page">
        {/* Hero */}
        <section className="py-16 px-4 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <FileText className="h-14 w-14 mx-auto mb-4 opacity-90" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 font-heading" data-testid="terms-title">Terms of Service</h1>
            <p className="text-lg text-slate-300">Please read these terms carefully before using Ariskool</p>
            <p className="text-sm text-slate-400 mt-3">Last updated: February 2026 | Governed by Indian Law</p>
          </div>
        </section>

        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Quick Summary */}
            <div className="bg-teal-50 border border-indigo-100 rounded-xl p-6 mb-12">
              <h3 className="font-bold text-slate-900 mb-3">Summary (Plain Language)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {[
                  'You must provide accurate school and admin details',
                  'We provide a leave management SaaS — you keep ownership of your data',
                  'Plans are billed monthly in INR with 7-day free trial',
                  'Don\'t misuse the platform or violate Indian laws',
                  'We can suspend accounts for non-payment or violations',
                  'Disputes are handled under Indian jurisdiction (Bangalore)'
                ].map((text, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <CheckCircle className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="prose prose-slate max-w-none space-y-10">
              {/* 1. Acceptance */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <span className="font-bold text-slate-600">1</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">Acceptance of Terms</h2>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  By accessing or using Ariskool ("Platform"), operated by Ariskool Technologies Pvt. Ltd. 
                  ("Company"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, 
                  you must not use the Platform. These Terms constitute a legally binding agreement under the 
                  <strong> Indian Contract Act, 1872</strong> and the <strong>Information Technology Act, 2000</strong>.
                </p>
              </section>

              {/* 2. Description */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <span className="font-bold text-slate-600">2</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">Description of Service</h2>
                </div>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Ariskool is a cloud-based leave management platform designed for Indian educational institutions, providing:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    'Leave request and approval workflows',
                    'Leave balance tracking and policies',
                    'In-app notification system',
                    'Staff/user management with roles',
                    'Analytics and report generation',
                    'Subscription billing (INR)'
                  ].map((feat, i) => (
                    <div key={i} className="flex gap-2 items-center p-2 bg-slate-50 rounded-lg text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700">{feat}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* 3. Accounts */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <span className="font-bold text-slate-600">3</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">User Accounts & Registration</h2>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-4 mb-2">Eligibility</h3>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>You must be at least 18 years old</li>
                  <li>You must be authorised to register on behalf of your school/institution</li>
                  <li>One primary account per school (multiple users within the tenant)</li>
                </ul>
                <h3 className="text-lg font-semibold text-slate-900 mt-5 mb-2">Your Responsibilities</h3>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>Provide accurate school details — name, state, district, board affiliation</li>
                  <li>Maintain the confidentiality of your login credentials</li>
                  <li>Notify us immediately at <a href="mailto:support@ariskool.in" className="text-teal-600 hover:underline">support@ariskool.in</a> if you suspect unauthorised access</li>
                  <li>You are responsible for all activities under your account</li>
                </ul>
              </section>

              {/* 4. Plans & Payments */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-slate-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">4. Subscription Plans & Payment</h2>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-4 mb-2">Plans (all prices in INR, excl. GST at 18%)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm mt-2">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 font-semibold text-slate-900 rounded-tl-lg">Plan</th>
                        <th className="text-left p-3 font-semibold text-slate-900">Price</th>
                        <th className="text-left p-3 font-semibold text-slate-900">Users</th>
                        <th className="text-left p-3 font-semibold text-slate-900 rounded-tr-lg">Ideal For</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100"><td className="p-3">Trial</td><td className="p-3">Free (7 days)</td><td className="p-3">Up to 10</td><td className="p-3">Evaluation</td></tr>
                      <tr className="border-b border-slate-100 bg-slate-50"><td className="p-3">Starter</td><td className="p-3 font-semibold">₹1,999/month</td><td className="p-3">Up to 50</td><td className="p-3">Small schools</td></tr>
                      <tr className="border-b border-slate-100"><td className="p-3">Professional</td><td className="p-3 font-semibold">₹4,999/month</td><td className="p-3">Up to 500</td><td className="p-3">Growing schools</td></tr>
                      <tr><td className="p-3">Enterprise</td><td className="p-3 font-semibold">₹14,999/month</td><td className="p-3">Up to 10,000</td><td className="p-3">School chains/trusts</td></tr>
                    </tbody>
                  </table>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-5 mb-2">Billing Terms</h3>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>Billing is monthly via Stripe India (supports UPI, cards, net banking)</li>
                  <li>GST at 18% is applicable on all paid plans</li>
                  <li>No refunds for partial months — cancel anytime, access continues until period end</li>
                  <li>Annual billing available at 20% discount — contact sales</li>
                  <li>Government/aided schools may be eligible for special pricing</li>
                </ul>
              </section>

              {/* 5. Acceptable Use */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">5. Acceptable Use Policy</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-emerald-700 flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4" /> You MAY
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700">
                      <li>Use the Platform for legitimate school leave management</li>
                      <li>Create user accounts for your school's staff</li>
                      <li>Export your data for backup or migration</li>
                      <li>Integrate with approved third-party tools via APIs</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-700 flex items-center gap-2 mb-2">
                      <XCircle className="h-4 w-4" /> You MUST NOT
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700">
                      <li>Violate any Indian law or regulation</li>
                      <li>Submit false or fraudulent leave requests</li>
                      <li>Attempt to access other tenants' data</li>
                      <li>Reverse-engineer, scrape, or copy the Platform</li>
                      <li>Transmit malware, viruses, or harmful code</li>
                      <li>Use the Platform for any non-educational purpose</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 6. Data Ownership */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <span className="font-bold text-slate-600">6</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">Data Ownership & Intellectual Property</h2>
                </div>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li><strong>Your Data:</strong> You retain full ownership of all data submitted to the Platform (leave records, user info, documents)</li>
                  <li><strong>Our Licence:</strong> You grant us a limited licence to process your data solely to operate the Platform</li>
                  <li><strong>Export:</strong> You can export all your data at any time in standard formats (CSV, PDF)</li>
                  <li><strong>Platform IP:</strong> The Platform's software, design, and branding are owned by Ariskool Technologies Pvt. Ltd. and protected under the Copyright Act, 1957</li>
                </ul>
              </section>

              {/* 7. SLA */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-slate-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">7. Service Availability</h2>
                </div>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>We target <strong>99.9% uptime</strong> for all paid plans</li>
                  <li>Scheduled maintenance will be communicated 48 hours in advance</li>
                  <li>Enterprise plans include a formal SLA with credits for downtime</li>
                  <li>We are not liable for disruptions caused by ISP issues, government-ordered shutdowns, or force majeure events</li>
                </ul>
              </section>

              {/* 8. Limitation of Liability */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">8. Limitation of Liability</h2>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <p className="text-slate-700 text-sm leading-relaxed m-0">
                    To the maximum extent permitted by Indian law, Ariskool Technologies Pvt. Ltd. shall not be liable 
                    for any indirect, incidental, special, consequential, or punitive damages arising from your use of the 
                    Platform. Our total liability shall not exceed the amount paid by you in the preceding 12 months.
                  </p>
                </div>
              </section>

              {/* 9. Termination */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <span className="font-bold text-slate-600">9</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">Termination</h2>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-4 mb-2">By You</h3>
                <p className="text-slate-700">Cancel anytime from Settings. Your access continues until the current billing period ends. Data is retained for 90 days post-cancellation.</p>
                <h3 className="text-lg font-semibold text-slate-900 mt-4 mb-2">By Us</h3>
                <p className="text-slate-700 mb-2">We may suspend or terminate your account for:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>Violation of these Terms or Acceptable Use Policy</li>
                  <li>Non-payment beyond 15 days of due date</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Inactivity for more than 12 months (free tier)</li>
                </ul>
              </section>

              {/* 10. Governing Law */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Scale className="h-5 w-5 text-slate-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">10. Governing Law & Dispute Resolution</h2>
                </div>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>These Terms are governed by the <strong>laws of India</strong></li>
                  <li>Any disputes shall be subject to the exclusive jurisdiction of courts in <strong>Bangalore, Karnataka</strong></li>
                  <li>Before litigation, parties agree to attempt resolution through mediation within 30 days</li>
                  <li>Applicable laws: Indian Contract Act 1872, IT Act 2000, Consumer Protection Act 2019, DPDP Act 2023</li>
                </ul>
              </section>

              {/* 11. Changes */}
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">11. Changes to These Terms</h2>
                <p className="text-slate-700 leading-relaxed">
                  We may update these Terms from time to time. Material changes will be communicated via email and in-app 
                  notification at least 30 days before taking effect. Continued use of the Platform after changes constitutes acceptance.
                </p>
              </section>

              {/* 12. Contact */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">12. Contact</h2>
                </div>
                <div className="bg-slate-50 rounded-lg p-6">
                  <p className="text-slate-700 m-0 mb-2">For questions about these Terms, contact:</p>
                  <p className="text-slate-700 m-0"><strong>Email:</strong> <a href="mailto:legal@ariskool.in" className="text-teal-600 hover:underline">legal@ariskool.in</a></p>
                  <p className="text-slate-700 m-0"><strong>Phone:</strong> +91 80 4567 8901</p>
                  <p className="text-slate-700 m-0"><strong>Address:</strong> Ariskool Technologies Pvt. Ltd., HSR Layout, Sector 2, Bangalore, Karnataka 560102, India</p>
                </div>
              </section>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Terms;
